import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { StripePayment } from "@/components/StripePayment";
import { BankTransfer } from "@/components/BankTransfer";
import { PickupPass } from "@/components/PickupPass";
import { toast } from "sonner";
import { CheckCircle2, Clock, CreditCard } from "lucide-react";

export default function Checkout() {
  const nav = useNavigate();
  const { cart, user, country, countries, currency, setCurrency, clearCart, format } = useApp();
  const [step, setStep] = useState("shipping"); // shipping | payment | success
  const [order, setOrder] = useState(null);
  const [quote, setQuote] = useState(null);
  const [promo, setPromo] = useState("");
  const [appliedPromo, setAppliedPromo] = useState("");
  const shippingMethod = "standard";
  const [loyalty, setLoyalty] = useState(null);
  const [usePoints, setUsePoints] = useState(0);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState("stripe");
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    email: user?.email || "",
    full_name: user?.name || "",
    line1: "", line2: "", city: "", region: "",
    postal_code: "", country: country || "PT", phone: "",
    nif: "", bi_number: "",
  });

  const isAO = form.country === "AO";
  const isPT = form.country === "PT";
  useEffect(() => { setCurrency(form.country === "AO" ? "AOA" : "EUR"); }, [form.country, setCurrency]);

  const items = useMemo(() => cart.map((i) => ({
    product_id: i.product_id, quantity: i.quantity, color: i.color, size: i.size,
  })), [cart]);

  const fetchQuote = async () => {
    if (cart.length === 0) return;
    try {
      const { data } = await api.post("/orders/quote", {
        items, email: form.email || "guest@voltix.com",
        shipping_address: form, currency, promo_code: appliedPromo || null, shipping_method: shippingMethod,
        use_loyalty_points: usePoints,
      });
      setQuote(data);
    } catch { setQuote(null); }
  };

  useEffect(() => { fetchQuote(); /* eslint-disable-next-line */ }, [form.country, shippingMethod, appliedPromo, cart, currency, usePoints]);
  useEffect(() => { if (user) api.get("/loyalty").then((r) => setLoyalty(r.data)).catch(() => {}); }, [user]);

  useEffect(() => {
    api.get("/payment-methods", { params: { country: form.country } }).then((r) => {
      setPaymentMethods(r.data.methods);
      if (!r.data.methods.find((m) => m.id === paymentMethod && m.available)) {
        setPaymentMethod(r.data.methods.find((m) => m.available)?.id || "");
      }
    });
    // eslint-disable-next-line
  }, [form.country]);

  const applyPromo = async () => {
    try {
      await api.post("/promos/validate", { code: promo, subtotal_eur: cart.reduce((a,i) => a + i.unit_price_eur * i.quantity, 0), country: form.country });
      setAppliedPromo(promo.toUpperCase());
      toast.success("Código aplicado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Código inválido");
    }
  };

  const placeOrder = async () => {
    if (!form.email || !form.full_name) { toast.error("Preenche nome e email"); return; }
    if (isAO && (!form.phone || !form.bi_number)) { toast.error("Angola requer telefone e nº BI"); return; }
    if (isPT && (!form.line1 || !form.city || !form.postal_code)) { toast.error("Portugal requer morada, cidade e código postal"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/orders", {
        items, email: form.email, shipping_address: form,
        currency, promo_code: appliedPromo || null, shipping_method: shippingMethod,
        use_loyalty_points: usePoints, payment_method: paymentMethod,
      });
      if (data.access_token) localStorage.setItem(`voltix_order_${data.number}`, data.access_token);
      setOrder(data);
      setStep("payment");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally { setBusy(false); }
  };

  const payFirst = async () => {
    setBusy(true);
    try {
      await api.post(`/orders/${order.number}/confirm-payment`, {
        provider: paymentMethod, installment: 1, simulated: true,
      });
      const { data } = await api.get(`/orders/${order.number}`);
      setOrder(data);
      clearCart();
      setStep("success");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setBusy(false); }
  };

  if (cart.length === 0 && step !== "success") {
    return <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="text-3xl font-display font-bold mb-4">Carrinho vazio</h1>
      <Button onClick={() => nav("/produtos")} className="rounded-full btn-primary">Ver produtos</Button>
    </div>;
  }

  if (step === "success" && order) {
    const second = order.installments?.find((i) => i.number === 2);
    return (
      <div className="mx-auto max-w-2xl px-4 py-20 text-center">
        <CheckCircle2 className="w-16 h-16 mx-auto text-green-500 mb-6" />
        <h1 className="text-4xl font-display font-bold mb-4">1.ª parcela simulada</h1>
        <p className="text-sm text-muted-foreground mb-6" data-testid="checkout-demo-success">DEMONSTRAÇÃO — não foi efetuada qualquer cobrança.</p>

        <PickupPass order={order} />

        <p className="text-muted-foreground mb-2">Número da encomenda:</p>
        <p className="font-mono text-xl font-bold text-primary mb-6" data-testid="order-number">{order.number}</p>
        <div className="rounded-2xl border border-border p-6 bg-card text-left mb-6">
          <div className="flex items-center gap-2 mb-3"><Clock className="w-4 h-4 text-primary" /><span className="font-semibold text-sm">Próximos passos</span></div>
          <ol className="text-sm space-y-2 list-decimal list-inside">
            <li>Entrega estimada em <strong>7 dias</strong></li>
            <li>Quando o estafeta chegar, mostra o QR ou o código para validação.</li>
            <li>Paga a <strong>2.ª parcela ({second?.amount.toFixed(2)} {second?.currency})</strong> na página da encomenda, antes de receberes o produto.</li>
          </ol>
        </div>
        <div className="flex gap-3 justify-center">
          <Button onClick={() => nav(`/encomenda/${order.number}`)} className="rounded-full btn-primary" data-testid="success-continue">Ver encomenda e QR</Button>
          <Button variant="outline" onClick={() => nav("/produtos")} className="rounded-full">Ver mais produtos</Button>
        </div>
      </div>
    );
  }

  const firstAmount = quote ? Math.round(quote.pricing.total * 50) / 100 : 0;

  return (
    <div className="mx-auto max-w-6xl px-4 py-12">
      <h1 className="text-4xl font-display font-bold mb-8">Finalizar compra</h1>

      <div className="mb-6 rounded-2xl border border-primary/30 bg-primary/5 p-4 flex items-start gap-3" data-testid="installment-notice">
        <CreditCard className="w-5 h-5 text-primary shrink-0 mt-0.5" />
        <div className="text-sm">
          <div className="font-semibold">Pagamento em 2 parcelas</div>
        <div className="text-muted-foreground">Pagas 50% agora e 50% com o estafeta presente, após validar o QR/código e antes da entrega.</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          {step === "shipping" && (
            <div className="rounded-2xl border border-border p-6 bg-card space-y-4">
              <h3 className="font-display font-semibold text-lg">Contacto e entrega</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Email *</Label><Input value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} data-testid="email-input" /></div>
                <div><Label>Nome completo *</Label><Input value={form.full_name} onChange={(e)=>setForm({...form,full_name:e.target.value})} data-testid="fullname-input" /></div>
                <div className="sm:col-span-2">
                  <Label>País *</Label>
                  <Select value={form.country} onValueChange={(v)=>{setForm((previous) => ({...previous,country:v})); setCurrency(v === "AO" ? "AOA" : "EUR");}}>
                    <SelectTrigger data-testid="country-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {countries.map((c) => <SelectItem key={c.code} value={c.code}>{c.flag} {c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {isAO && (
                  <>
                    <div><Label>Telefone *</Label><Input value={form.phone} placeholder="+244 ..." onChange={(e)=>setForm({...form,phone:e.target.value})} data-testid="phone-input" /></div>
                    <div><Label>Nº Bilhete de Identidade *</Label><Input value={form.bi_number} placeholder="000000000LA000" onChange={(e)=>setForm({...form,bi_number:e.target.value})} data-testid="bi-input" /></div>
                    <div className="sm:col-span-2 text-xs text-muted-foreground bg-muted/40 rounded-lg p-3">
                      🇦🇴 Para Angola só precisamos do teu nome, telefone e nº BI. Entregamos no endereço combinado por telefone.
                    </div>
                  </>
                )}

                {isPT && (
                  <>
                    <div className="sm:col-span-2"><Label>Morada *</Label><Input value={form.line1} onChange={(e)=>setForm({...form,line1:e.target.value})} data-testid="line1-input" /></div>
                    <div className="sm:col-span-2"><Label>Complemento</Label><Input value={form.line2} onChange={(e)=>setForm({...form,line2:e.target.value})} /></div>
                    <div><Label>Cidade *</Label><Input value={form.city} onChange={(e)=>setForm({...form,city:e.target.value})} data-testid="city-input" /></div>
                    <div><Label>Distrito</Label><Input value={form.region} onChange={(e)=>setForm({...form,region:e.target.value})} /></div>
                    <div><Label>Código postal *</Label><Input data-testid="postal-input" value={form.postal_code} placeholder="0000-000" onChange={(e)=>setForm({...form,postal_code:e.target.value})} /></div>
                    <div><Label>Telefone</Label><Input value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} /></div>
                    <div className="sm:col-span-2"><Label>NIF</Label><Input value={form.nif} placeholder="000000000" onChange={(e)=>setForm({...form,nif:e.target.value})} data-testid="nif-input" /></div>
                  </>
                )}
              </div>

              <div className="pt-4">
                <Label className="mb-2 block">Método de envio</Label>
                <div className="p-4 rounded-xl border border-primary bg-primary/5 text-sm" data-testid="ship-standard">Standard</div>
              </div>

              <div className="pt-2">
                <Label className="mb-2 block">Método de pagamento</Label>
                {isAO && <p className="text-xs text-muted-foreground mb-3" data-testid="angola-checkout-pending">MULTICAIXA em AOA: adesão bancária e testes oficiais ainda pendentes. Estes métodos não aceitam pagamentos por enquanto.</p>}
                <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="grid gap-3">
                  {paymentMethods.map((m) => (
                    <label key={m.id} className={`p-4 rounded-xl border cursor-pointer flex items-center gap-3 ${paymentMethod===m.id ? "border-primary bg-primary/5" : "border-border"}`}>
                      <RadioGroupItem value={m.id} disabled={!m.available} data-testid={`pay-method-${m.id}`} />
                      <div><span className="text-sm font-medium">{m.name}</span>{m.id === "stripe" && m.mbway_available && <p className="text-xs text-muted-foreground" data-testid="checkout-mbway-note">MB WAY também disponível no Stripe em pagamentos EUR elegíveis</p>}{!m.available && <p className="text-xs text-muted-foreground" data-testid={`payment-unavailable-${m.id}`}>{m.reason || "Ativação pendente"}</p>}</div>
                      {m.id === "multicaixa" && <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-primary/15 text-primary font-medium">🇦🇴 Angola</span>}
                    </label>
                  ))}
                </RadioGroup>
              </div>

              <Button size="lg" className="rounded-full btn-primary w-full mt-4" onClick={placeOrder} disabled={busy || !paymentMethods.some((m) => m.id === paymentMethod && m.available)} data-testid="place-order-btn">
                Rever pagamento
              </Button>
            </div>
          )}

          {step === "payment" && order && (
            <div className="rounded-2xl border border-border p-6 bg-card space-y-4">
              <h3 className="font-display font-semibold text-lg">Pagamento da 1ª parcela</h3>
              <p className="text-sm text-muted-foreground">Encomenda <span className="font-mono font-semibold text-foreground">{order.number}</span> criada. Confirma a 1ª parcela ({order.installments[0].amount.toFixed(2)} {order.currency}).</p>
              {order.payment_method === "stripe" ? <StripePayment order={order} installment={order.installments[0]} onPaid={() => nav(`/encomenda/${order.number}`)} /> : order.payment_method === "iban" ? <BankTransfer order={order} installment={order.installments[0]} onUpdate={async () => { clearCart(); nav(`/encomenda/${order.number}`); }} /> : <div className="rounded-xl border border-border p-4 bg-secondary/40">
                <div className="text-sm font-semibold mb-1">PayPal — DEMONSTRAÇÃO</div>
                <p className="text-xs text-muted-foreground mb-3">Teste do fluxo de pagamento, sem redirecionamento e sem cobrança real.</p>
                <Button className="w-full rounded-full btn-primary" onClick={payFirst} disabled={busy} data-testid="pay-first-btn">
                  {busy ? "A processar…" : "Simular 1.ª parcela"} — {order.installments[0].amount.toFixed(2)} {order.currency}
                </Button>
              </div>}
              <Button variant="outline" className="rounded-full w-full" data-testid="checkout-back" onClick={()=>setStep("shipping")}>Voltar</Button>
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-border p-6 h-fit bg-card space-y-3">
          <h3 className="font-display font-semibold text-lg">Resumo</h3>
          <div className="space-y-2 pb-3 border-b border-border">
            {cart.map((i) => (
              <div key={i.key} className="flex justify-between text-sm">
                <span className="truncate mr-2">{i.name} × {i.quantity}</span>
                <span>{format(i.unit_price_eur * i.quantity)}</span>
              </div>
            ))}
          </div>

          {step === "shipping" && (
            <div className="flex gap-2 pt-2">
              <Input placeholder="Código promocional" value={promo} onChange={(e)=>setPromo(e.target.value)} data-testid="promo-input" />
              <Button variant="outline" onClick={applyPromo} data-testid="apply-promo-btn">Aplicar</Button>
            </div>
          )}

          {step === "shipping" && loyalty && loyalty.points >= 100 && (
            <div className="pt-2" data-testid="loyalty-block">
              <div className="text-xs uppercase tracking-widest text-primary mb-2">
                Pontos VOLTIX
                <span className="text-muted-foreground normal-case tracking-normal ml-1">({loyalty.points} disponíveis · €{loyalty.value_eur.toFixed(2)})</span>
              </div>
              <div className="flex gap-2 items-center">
                <Input type="number" min="0" step="100" max={Math.floor(loyalty.points / 100) * 100}
                  value={usePoints} onChange={(e) => setUsePoints(Math.max(0, parseInt(e.target.value) || 0))}
                  data-testid="loyalty-input" />
                <Button type="button" size="sm" variant="outline" onClick={() => setUsePoints(Math.floor(loyalty.points / 100) * 100)} data-testid="loyalty-max-btn">Máx</Button>
              </div>
            </div>
          )}

          {quote && (
            <div className="space-y-1.5 text-sm pt-3">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{quote.pricing.subtotal.toFixed(2)} {quote.pricing.currency}</span></div>
              {quote.pricing.discount > 0 && <div className="flex justify-between text-green-600"><span>Desconto</span><span>-{quote.pricing.discount.toFixed(2)} {quote.pricing.currency}</span></div>}
              {quote.pricing.loyalty_discount > 0 && <div className="flex justify-between text-primary"><span>Pontos ({quote.pricing.loyalty_points_used})</span><span>-{quote.pricing.loyalty_discount.toFixed(2)} {quote.pricing.currency}</span></div>}
              <div className="flex justify-between"><span className="text-muted-foreground">Envio</span><span>{quote.pricing.shipping.toFixed(2)} {quote.pricing.currency}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">{paymentMethod === "stripe" ? "Impostos (estimativa incluída)" : "Impostos"}</span><span>{quote.pricing.tax.toFixed(2)} {quote.pricing.currency}</span></div>
              <div className="flex justify-between font-display font-bold text-lg pt-2 border-t border-border" data-testid="checkout-total">
                <span>Total</span><span>{quote.pricing.total.toFixed(2)} {quote.pricing.currency}</span>
              </div>
              <div className="mt-3 p-3 rounded-xl bg-primary/5 border border-primary/20 text-xs space-y-1">
                <div className="flex justify-between"><span className="font-semibold">1ª parcela (agora)</span><span className="font-mono font-bold">{firstAmount.toFixed(2)} {quote.pricing.currency}</span></div>
                <div className="flex justify-between text-muted-foreground"><span>2ª parcela (na entrega)</span><span className="font-mono">{(quote.pricing.total - firstAmount).toFixed(2)} {quote.pricing.currency}</span></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
